import React from "react";
import styles from "./styles.module.scss";
import { ArrowCircleRightIcon } from "../../Styles/Icons/DesignSystem";
import {
  format,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  isToday,
} from "date-fns";
import classNames from "classnames";
import { useSelector } from "react-redux";
import FallCardSkeleton from "./FallCardSkeleton";
import CardiologyIcon from "../../Styles/Icons/DesignSystem/CardiologyIcon";
import PulmonologyIcon from "../../Styles/Icons/DesignSystem/PulmonologyIcon";
import { mapBreathingStatus } from "./utils";
import { capitalize } from "@intelligentlilli/lilli-utils";
import { FallIcon } from "../../Styles/Icons/DesignSystem";
import FallStatusBanner from "./FallStatusBanner";

const FallCard = ({ fall, onClick, loading, recentFall = false }) => {
  const [hovering, setHovering] = React.useState(false);
  const [pressing, setPressing] = React.useState(false);
  const staff = useSelector((state) => state.staff);
  if (loading) {
    return <FallCardSkeleton room={fall.room} />;
  }
  const neverFallen = fall.currentStatus === "never-fallen";

  const detected = fall.currentStatus === "detected";
  const acknowledged = fall.currentStatus === "acknowledged";
  const resolved = fall.currentStatus === "resolved";

  // We only show the check required UI once the 30 minutes have passed i.e. the future flag is false
  const checkRequired =
    fall.currentStatus === "check_required" &&
    !fall.events[fall.events.length - 1].future;
  // In addition to the above checks we know it's the second follow up check if the first check has been completed
  const check2Required =
    fall.currentStatus === "check_required" &&
    !!fall.events.find((event) => event.status === "check_completed") &&
    !fall.events[fall.events.length - 1].future;

  const timeOfFall = fall.timestamp;

  const lastEvent = fall.events?.[fall.events.length - 1];
  const staffMemberAtended = staff.find((i) => i.id === lastEvent?.userId);

  const heartRateStatus = fall?.hrBreathingData?.heartRateStatus;
  const breathingRateStatus = fall?.hrBreathingData?.breathingStatus;

  const getFallTime = (ts) => {
    const date = new Date(ts);
    const now = new Date();

    // set seconds to 0 so there is not a disparity if a fall happened at 13:40:30 and the UI says fall occured 30 minutes ago at 14:11
    date.setSeconds(0, 0);
    now.setSeconds(0, 0);

    if (differenceInSeconds(now, date) < 60) {
      return "Just now";
    }

    if (isToday(date)) {
      const hours = differenceInHours(new Date(), date);
      const minutes = differenceInMinutes(new Date(), date) % 60;

      if (hours > 0) {
        return `${hours} ${hours === 1 ? "hr" : "hrs"} ${minutes} ${minutes === 1 ? "min" : "mins"} ago`;
      } else {
        return `${minutes} ${minutes === 1 ? "min" : "mins"} ago`;
      }
    } else {
      return `${format(date, "dd/MM/yyyy")}`;
    }
  };

  const formattedTimeOfFall = timeOfFall && getFallTime(timeOfFall);

  return (
    <button
      className={classNames(styles.card, {
        [styles.card_detected]: detected,
        [styles.card_acknowledged]: acknowledged || checkRequired,
      })}
      onMouseOver={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      // onPress style change
      onMouseDown={() => setPressing(true)}
      onMouseUp={() => setPressing(false)}
      onClick={onClick}
    >
      <div className={styles.row}>
        <h3>{fall.room}</h3>
        {detected || acknowledged || checkRequired || check2Required ? (
          <div className={styles.row_element}>
            {!neverFallen && <FallIcon width={20} />}
            <p className={styles.time_of_fall}>
              {!neverFallen ? formattedTimeOfFall : null}
            </p>
          </div>
        ) : null}
      </div>

      {/* We don't want the banner to show if the user has never fallen and if the fall has been resolved for more than 72 hours. We do want it to show if there has been a recent fall (< 72 hours)*/}
      {!neverFallen && (!resolved || (resolved && recentFall)) ? (
        <div className={styles.banner_row}>
          <FallStatusBanner
            detected={detected}
            attending={acknowledged}
            checkRequired={checkRequired && !check2Required}
            check2Required={check2Required}
            staffMemberAttended={staffMemberAtended}
            fallLocation={fall?.location}
            recentFall={recentFall}
            fallTime={formattedTimeOfFall}
          />
        </div>
      ) : null}
      <div className={styles.row}>
        <div
          className={classNames(styles.data_card, {
            [styles.danger]:
              heartRateStatus === "high" || heartRateStatus === "low",
            [styles.normal]: heartRateStatus === "normal",
            [styles.unknown]: heartRateStatus === undefined,
          })}
        >
          <div className={styles.sub_card_heading}>
            <CardiologyIcon width={16} />{" "}
            <p className={styles.sub_card_text}>Heart rate</p>
          </div>
          <h4>{capitalize(heartRateStatus ?? "unknown")}</h4>
          {fall?.hrBreathingData?.heartRate ? (
            <p className={styles.sub_card_text}>
              {fall?.hrBreathingData?.heartRate} bpm
            </p>
          ) : (
            <div className={styles.min_height} />
          )}
        </div>
        <div
          className={classNames(styles.data_card, {
            [styles.danger]:
              breathingRateStatus === "hypopnea" ||
              breathingRateStatus === "hyperpnea" ||
              breathingRateStatus === "apnea",
            [styles.normal]: breathingRateStatus === "normal",
            [styles.unknown]: breathingRateStatus === undefined,
          })}
        >
          <div className={styles.sub_card_heading}>
            <PulmonologyIcon width={16} />
            <p className={styles.sub_card_text}>Breathing rate</p>
          </div>
          <h4>{mapBreathingStatus(breathingRateStatus)}</h4>
          {fall?.hrBreathingData?.breathingRate ? (
            <p className={styles.sub_card_text}>
              {fall?.hrBreathingData?.breathingRate} br/min
            </p>
          ) : (
            <div className={styles.min_height} />
          )}
        </div>
      </div>
      <div className={classNames(styles.row)}>
        {onClick && (
          <div
            className={classNames({
              [styles.action_hover]: hovering,
              [styles.action]: !hovering,
              [styles.action_pressing]: pressing,
              [styles.action]: !pressing,
            })}
          >
            {detected || checkRequired || check2Required || acknowledged
              ? "Take action"
              : "View details"}
            <ArrowCircleRightIcon width={16} />
          </div>
        )}
      </div>
    </button>
  );
};

export default FallCard;
